import {
  type AutotuneParamsConfig,
  type HardwareConfig,
  type HardwareRevisionConfig,
  type InitialTuningConfig,
  isOperationalNumericConfigValue,
  normalizeConfigIdentifier,
  type TuningTargetConfig,
} from '../hardware-config/schema';

export type RetuneConfigErrorReason =
  | 'config_unavailable'
  | 'revision_not_found'
  | 'overlapping_windows'
  | 'invalid_priority_config'
  | 'no_operational_targets'
  | 'target_not_found';

export class RetuneConfigError extends Error {
  readonly reason: RetuneConfigErrorReason;

  constructor(reason: RetuneConfigErrorReason, message: string) {
    super(message);
    this.name = 'RetuneConfigError';
    this.reason = reason;
  }
}

export class RetuneUnsupportedHardwareError extends Error {
  readonly revision: string;

  constructor(revision: string) {
    super(`Retune unsupported for hardware revision "${revision}".`);
    this.name = 'RetuneUnsupportedHardwareError';
    this.revision = revision;
  }
}

export type OperationalInitialTuningConfig = {
  scan_start_freq_hz: number;
  scan_end_freq_hz: number;
  coarse_step_hz: number;
  fine_step_hz: number;
  fine_scan_bandwidth_hz: number;
  dwell_time_ms: number;
  exit_criteria: string;
};

export type OperationalAutotuneParamsConfig = {
  initial_step_size_hz: number;
  max_bandwidth_hz: number;
  min_peak_power_mw: number;
  noise_threshold_mw: number;
  sharp_drop_threshold: number;
  max_iterations: number;
  settle_time_ms: number;
  convergence_power_threshold_mw: number;
  convergence_freq_threshold_hz: number;
  autotune_seek_maximum: boolean;
};

export type OperationalTuningTargetConfig = Omit<
  TuningTargetConfig,
  'center_frequency_hz' | 'search_bandwidth_hz' | 'initial_tuning' | 'autotune_params'
> & {
  center_frequency_hz: number;
  search_bandwidth_hz: number;
  initial_tuning: OperationalInitialTuningConfig;
  autotune_params: OperationalAutotuneParamsConfig;
};

export type SelectedTarget = {
  revision: HardwareRevisionConfig;
  target: OperationalTuningTargetConfig;
  source: 'window-match' | 'priority-fallback';
};

export type ResolveRetuneRevisionInput = {
  config: HardwareConfig | null | undefined;
  revision: string | null | undefined;
};

export type SelectRetuneTargetInput = {
  revision: HardwareRevisionConfig;
  currentCenterHz: number;
};

export type ResolveRetuneTargetsInput = ResolveRetuneRevisionInput & {
  currentCenterHz: number;
};

export type ResolveRetuneTargetByIdInput = {
  revision: HardwareRevisionConfig;
  targetId: string | null | undefined;
};

export type ResolvedRetuneTargets = {
  revision: HardwareRevisionConfig;
  targets: OperationalTuningTargetConfig[];
  defaultTargetId: string;
  defaultTargetSource: SelectedTarget['source'];
};

export function resolveRetuneRevision(input: ResolveRetuneRevisionInput): HardwareRevisionConfig {
  if (!input.config) {
    throw new RetuneConfigError('config_unavailable', 'Retune configuration is unavailable.');
  }

  const reportedRevision = normalizeReportedRevision(input.revision);
  const normalizedReportedRevision = normalizeConfigIdentifier(reportedRevision);

  const matchedRevision = input.config.hardware_revisions.find(
    (candidate) => normalizeConfigIdentifier(candidate.revision_id) === normalizedReportedRevision
  );

  if (!matchedRevision) {
    throw new RetuneUnsupportedHardwareError(reportedRevision);
  }

  return matchedRevision;
}

export function selectRetuneTarget(input: SelectRetuneTargetInput): SelectedTarget {
  const { revision, targets } = resolveRetuneTargetsForRevision(input.revision, input.currentCenterHz);
  const currentCenterHz = Math.round(input.currentCenterHz);
  const matchingTargets = targets.filter((target) => isFrequencyWithinTargetWindow(target, currentCenterHz));
  const fallbackTarget = targets[0];

  if (!fallbackTarget) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Revision "${revision.revision_id}" has no operational tuning targets.`
    );
  }

  if (matchingTargets.length === 1) {
    return {
      revision,
      target: matchingTargets[0],
      source: 'window-match',
    };
  }

  return {
    revision,
    target: fallbackTarget,
    source: 'priority-fallback',
  };
}

export function resolveRetuneTargets(input: ResolveRetuneTargetsInput): ResolvedRetuneTargets {
  const revision = resolveRetuneRevision(input);
  const selected = selectRetuneTarget({
    revision,
    currentCenterHz: input.currentCenterHz,
  });
  const targets = getOperationalRetuneTargets(revision);

  return {
    revision,
    targets,
    defaultTargetId: selected.target.target_id,
    defaultTargetSource: selected.source,
  };
}

export function resolveRetuneTargetById(input: ResolveRetuneTargetByIdInput): OperationalTuningTargetConfig {
  const normalizedTargetId = normalizeConfigIdentifier(input.targetId ?? '');
  if (!normalizedTargetId) {
    throw new RetuneConfigError(
      'target_not_found',
      `Retune target selection is required for revision "${input.revision.revision_id}".`
    );
  }

  assertRevisionPriorities(input.revision);
  const target = input.revision.tuning_targets
    .map((candidate) => toOperationalTarget(candidate))
    .filter((candidate): candidate is OperationalTuningTargetConfig => candidate != null)
    .find(
    (candidate) => normalizeConfigIdentifier(candidate.target_id) === normalizedTargetId
    );

  if (!target) {
    throw new RetuneConfigError(
      'target_not_found',
      `Retune target "${input.targetId}" was not found in revision "${input.revision.revision_id}".`
    );
  }

  return target;
}

export function getOperationalRetuneTargets(
  revision: HardwareRevisionConfig
): OperationalTuningTargetConfig[] {
  assertRevisionPriorities(revision);

  const operationalTargets = revision.tuning_targets
    .map((target) => toOperationalTarget(target))
    .filter((target): target is OperationalTuningTargetConfig => target != null)
    .sort((left, right) => left.priority - right.priority);

  if (operationalTargets.length === 0) {
    throw new RetuneConfigError(
      'no_operational_targets',
      `Revision "${revision.revision_id}" has no operational tuning targets.`
    );
  }

  return operationalTargets;
}

function toOperationalTarget(target: TuningTargetConfig): OperationalTuningTargetConfig | null {
  if (!isOperationalNumericConfigValue(target.center_frequency_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(target.search_bandwidth_hz)) {
    return null;
  }

  const initialTuning = toOperationalInitialTuning(target.initial_tuning);
  if (!initialTuning) {
    return null;
  }

  const autotuneParams = toOperationalAutotune(target.autotune_params);
  if (!autotuneParams) {
    return null;
  }

  if (target.search_bandwidth_hz < 0) {
    return null;
  }

  return {
    ...target,
    center_frequency_hz: target.center_frequency_hz,
    search_bandwidth_hz: target.search_bandwidth_hz,
    initial_tuning: initialTuning,
    autotune_params: autotuneParams,
  };
}

function toOperationalInitialTuning(data: InitialTuningConfig): OperationalInitialTuningConfig | null {
  if (!isOperationalNumericConfigValue(data.scan_start_freq_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.scan_end_freq_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.coarse_step_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.fine_step_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.fine_scan_bandwidth_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.dwell_time_ms)) {
    return null;
  }

  return {
    scan_start_freq_hz: data.scan_start_freq_hz,
    scan_end_freq_hz: data.scan_end_freq_hz,
    coarse_step_hz: data.coarse_step_hz,
    fine_step_hz: data.fine_step_hz,
    fine_scan_bandwidth_hz: data.fine_scan_bandwidth_hz,
    dwell_time_ms: data.dwell_time_ms,
    exit_criteria: data.exit_criteria,
  };
}

function toOperationalAutotune(data: AutotuneParamsConfig): OperationalAutotuneParamsConfig | null {
  if (!isOperationalNumericConfigValue(data.initial_step_size_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.max_bandwidth_hz)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.min_peak_power_mw)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.noise_threshold_mw)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.sharp_drop_threshold)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.max_iterations)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.settle_time_ms)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.convergence_power_threshold_mw)) {
    return null;
  }

  if (!isOperationalNumericConfigValue(data.convergence_freq_threshold_hz)) {
    return null;
  }

  return {
    initial_step_size_hz: data.initial_step_size_hz,
    max_bandwidth_hz: data.max_bandwidth_hz,
    min_peak_power_mw: data.min_peak_power_mw,
    noise_threshold_mw: data.noise_threshold_mw,
    sharp_drop_threshold: data.sharp_drop_threshold,
    max_iterations: data.max_iterations,
    settle_time_ms: data.settle_time_ms,
    convergence_power_threshold_mw: data.convergence_power_threshold_mw,
    convergence_freq_threshold_hz: data.convergence_freq_threshold_hz,
    autotune_seek_maximum: data.autotune_seek_maximum,
  };
}

function normalizeReportedRevision(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : 'unknown';
}

function resolveRetuneTargetsForRevision(
  revision: HardwareRevisionConfig,
  currentCenterHz: number
): { revision: HardwareRevisionConfig; targets: OperationalTuningTargetConfig[] } {
  const targets = getOperationalRetuneTargets(revision);
  const matchingTargets = targets.filter((target) =>
    isFrequencyWithinTargetWindow(target, Math.round(currentCenterHz))
  );

  if (matchingTargets.length > 1) {
    throw new RetuneConfigError(
      'overlapping_windows',
      `Multiple tuning targets match center frequency ${Math.round(currentCenterHz)} Hz in revision "${revision.revision_id}".`
    );
  }

  return {
    revision,
    targets,
  };
}

function assertRevisionPriorities(revision: HardwareRevisionConfig): void {
  const priorities = new Set<number>();
  for (const target of revision.tuning_targets) {
    if (!Number.isInteger(target.priority) || target.priority < 0) {
      throw new RetuneConfigError(
        'invalid_priority_config',
        `Target "${target.target_id}" in revision "${revision.revision_id}" has invalid priority "${target.priority}".`
      );
    }
    if (priorities.has(target.priority)) {
      throw new RetuneConfigError(
        'invalid_priority_config',
        `Revision "${revision.revision_id}" has duplicate priority "${target.priority}".`
      );
    }
    priorities.add(target.priority);
  }
}

function isFrequencyWithinTargetWindow(
  target: OperationalTuningTargetConfig,
  frequencyHz: number
): boolean {
  const lowHz = target.center_frequency_hz - target.search_bandwidth_hz;
  const highHz = target.center_frequency_hz + target.search_bandwidth_hz;
  return frequencyHz >= lowHz && frequencyHz <= highHz;
}
